import { LitElement, html, css } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { setPageTitle } from '../lib/page-title';

/**
 * Visual QA page for the deck-system text primitives. Embeds the static
 * `components/typography.html` — keeping the source of truth in pure
 * HTML + CSS means the page works on file:// (no Lit dependency) and
 * the in-app /typography route just iframes it.
 *
 * Theme bridge mirrors deck-viewer-page (Phase B): the iframe sandbox
 * drops `allow-same-origin`, so the parent posts `deck:theme` on every
 * theme flip and on the iframe's load event. deck-bridge.js inside the
 * iframe applies the message.
 */
@customElement('typography-page')
export class TypographyPage extends LitElement {
  @query('iframe') private iframeEl?: HTMLIFrameElement;

  static styles = css`
    :host {
      display: block;
      position: fixed;
      inset: 64px 0 0 0;
      background: var(--canvas);
    }
    iframe {
      width: 100%;
      height: 100%;
      border: 0;
      display: block;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    setPageTitle('Typography');
    window.addEventListener('themechange', this.onThemeChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('themechange', this.onThemeChange);
  }

  private currentTheme(): 'light' | 'dark' {
    const t = document.documentElement.getAttribute('data-theme');
    return t === 'dark' ? 'dark' : 'light';
  }

  private postThemeToIframe(theme: 'light' | 'dark') {
    try {
      this.iframeEl?.contentWindow?.postMessage({ type: 'deck:theme', theme }, '*');
    } catch (_e) { /* defensive */ }
  }

  private onThemeChange = (e: Event) => {
    const detail = (e as CustomEvent<{ theme: 'light' | 'dark' }>).detail;
    this.postThemeToIframe(detail?.theme ?? this.currentTheme());
  };

  private onIframeLoad = () => {
    this.postThemeToIframe(this.currentTheme());
    // Move focus into the iframe so the deck's keyboard shortcuts work.
    this.iframeEl?.focus();
  };

  render() {
    return html`
      <iframe
        title="Typography reference"
        src="/components/typography.html"
        sandbox="allow-scripts allow-popups allow-forms allow-modals"
        allow="fullscreen"
        @load=${this.onIframeLoad}
      ></iframe>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'typography-page': TypographyPage;
  }
}
